import i18n from 'i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import { initReactI18next } from 'react-i18next';

import { getBrowserLocale } from './browser-locale';
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  SUPPORTED_LOCALES_MAP,
  isLocaleSupported,
  type SupportedLocale,
} from './locale-constants';
import enLogin from './locales/en/login.json';
import frCALogin from './locales/fr-CA/login.json';

export {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  SUPPORTED_LOCALES_MAP,
  isLocaleSupported,
  type SupportedLocale,
};

// Namespace definitions for type safety
export const NAMESPACE = {
  ADMIN: 'admin',
  ADMIN_NOTIFICATIONS: 'adminNotifications',
  AGENTIC_FRAMEWORK: 'agenticFramework',
  AI_FEEDBACK: 'aiFeedback',
  APP_SHELL: 'appShell',
  BRANDS: 'brands',
  CASE_DETAILS: 'caseDetails',
  CASE_PROGRESS: 'caseProgress',
  CASE_SUMMARY: 'caseSummary',
  CFNOL: 'cfnol',
  CHAT: 'chat',
  CLAIMS: 'claims',
  COMMON: 'common',
  CONFIG_EXPORTS: 'configExports',
  CONFIRMATION_MODALS: 'confirmationModals',
  COPART: 'copart',
  CREATE_CASE: 'createCase',
  DATE: 'date',
  EVENT_LOGS: 'eventLogs',
  INACTIVE_AUTO_REPLY: 'inactiveAutoReply',
  INBOX: 'inbox',
  INSIGHTS: 'insights',
  INTEGRATIONS: 'integrations',
  LANGUAGES: 'languages',
  LOGIN: 'login',
  MEDIA: 'media',
  MEDIA_RESTRICTIONS: 'mediaRestrictions',
  MEDIA_VIEWER: 'mediaViewer',
  MESSAGE_PREFIXES: 'messagePrefixes',
  MESSAGE_TEMPLATES: 'messageTemplates',
  NOTES: 'notes',
  OUTREACH: 'outreach',
  OUTREACH_V2: 'outreachV2',
  PERSONAL_SETTINGS: 'personalSettings',
  PARTICIPANTS: 'participants',
  POLICIES: 'policies',
  QUEUES: 'queues',
  RIGHT_BAR: 'rightBar',
  SECURITY: 'security',
  SSO: 'sso',
  SYSTEM_MESSAGES: 'systemMessages',
  TEAM_MANAGEMENT: 'teamManagement',
  TOASTS: 'toasts',
  VALIDATION: 'validation',
  VERISK: 'verisk',
  VOICE_MESSAGE: 'voiceMessage',
  WORKFLOWS: 'workflows',
  WRITING_ASSISTANT: 'writingAssistant',
} as const;

export const NAMESPACES = Object.values(NAMESPACE);
export type Namespace = (typeof NAMESPACE)[keyof typeof NAMESPACE];

// Initialize i18next with lazy loading
// Translations are loaded on-demand using webpack dynamic imports
// See: https://www.i18next.com/how-to/add-or-load-translations#lazy-load-in-memory-translations
i18n
  .use(
    resourcesToBackend(
      (language: string, namespace: string) =>
        import(`./locales/${language}/${namespace}.json`),
    ),
  )
  .use(initReactI18next)
  .init({
    lng: getBrowserLocale(),
    fallbackLng: DEFAULT_LOCALE,
    defaultNS: 'common',
    ns: NAMESPACES,

    resources: {
      en: { login: enLogin },
      'fr-CA': { login: frCALogin },
    },
    partialBundledLanguages: true,

    interpolation: {
      escapeValue: false, // React already escapes values
    },

    // Development options
    debug: process.env.NODE_ENV === 'development',

    // React options
    react: {
      useSuspense: false, // Disable suspense for now to avoid loading states
    },
  });

/**
 * Helper function to change the current language.
 * Exposed on window object for visual testing during development.
 *
 * When called, this will trigger lazy loading of the requested locale's
 * translation files if they haven't been loaded yet.
 *
 * @param locale - The locale to switch to (e.g., 'en', 'fr-CA')
 * @returns Promise that resolves when language is changed and loaded
 *
 * @example
 * // In browser console (development only):
 * window.changeLanguage('fr-CA')
 */
export const changeLanguage = (locale: SupportedLocale): Promise<void> => {
  if (!SUPPORTED_LOCALES.includes(locale)) {
    // eslint-disable-next-line no-console
    console.warn(
      `Unsupported locale: ${locale}. Supported locales: ${SUPPORTED_LOCALES.join(', ')}`,
    );
    return Promise.resolve();
  }
  if (i18n.language === locale) {
    return Promise.resolve();
  }
  return i18n.changeLanguage(locale).then(() => {
    // eslint-disable-next-line no-console
    console.info(`Language changed to: ${locale}`);
  });
};

// Expose changeLanguage on window for development/demo purposes only
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (
    window as unknown as Window & { changeLanguage: typeof changeLanguage }
  ).changeLanguage = changeLanguage;
}

/**
 * Get the current language
 */
export const getCurrentLanguage = (): string => i18n.language;

// Toasts-namespace t() that also loads the toasts + common bundles.
export const makeToastT = () => {
  i18n.loadNamespaces([NAMESPACE.TOASTS, NAMESPACE.COMMON]);
  return (key: string, vars?: Record<string, unknown>) =>
    i18n.t(key, { ns: NAMESPACE.TOASTS, ...vars });
};

export default i18n;
